import React from 'react';

import { Box, Button, Typography } from '@mui/material';

import { Field, Form } from '@hrlogics/react-components/form';

import {
  ClearVerifyStaffRole,
  PlatformStaffListDocument,
  useSendInvitesToClearVerifyStaffMutation,
} from 'src/api';
import { AutocompleteField } from 'src/components/finalForm';
import { EMAILS_ADDRESSES_FIELD_HELPER_TEXT } from 'src/constants/misc';
import { useNotification } from 'src/hooks/app';

import { ClearVerifyStaffAccessFormPart } from '../ClearVerifyStaffAccessFormPart';

import { InviteFormFields, InviteFormValues } from './types';
import { inviteFormValidationSchema } from './utils';

interface ClearVerifyStaffInviteManagersFormProps {
  closeModal: () => void;
  switchClosability: (isClosureAllowed: boolean) => void;
}

export const ClearVerifyStaffInviteManagersForm: React.FC<
  ClearVerifyStaffInviteManagersFormProps
> = ({ closeModal, switchClosability }) => {
  const { createApolloContext } = useNotification();

  const [sendInvitesToClearVerifyStaffMutation] = useSendInvitesToClearVerifyStaffMutation({
    refetchQueries: [PlatformStaffListDocument],
    context: createApolloContext({ successMessage: 'Invitations sent' }),
  });

  const handleClose = React.useCallback(() => closeModal(), [closeModal]);

  const onSubmit = React.useCallback(
    async ({ emails, ...access }: InviteFormValues) => {
      switchClosability(false);

      try {
        await sendInvitesToClearVerifyStaffMutation({
          variables: {
            managers: emails.map(email => ({ email })),
            access,
          },
        });

        switchClosability(true);
        closeModal();
      } catch {
        switchClosability(true);
      }
    },
    [sendInvitesToClearVerifyStaffMutation, closeModal, switchClosability],
  );

  return (
    <Box>
      <Typography variant="body1" fontWeight="bold">
        Invite staff by using their email address
      </Typography>

      <Form<InviteFormValues>
        initialValues={initialValues}
        onSubmit={onSubmit}
        subscription={{ valid: true, submitting: true }}
        containerProps={{ mt: 4 }}
        validationSchema={inviteFormValidationSchema}
      >
        {({ submitting, valid }) => {
          return (
            <React.Fragment>
              <Box>
                <Field
                  name={InviteFormFields.emails}
                  component={AutocompleteField}
                  label="Email addresses"
                  fullWidth
                  multiple
                  freeSolo
                  options={[]}
                  helperText={EMAILS_ADDRESSES_FIELD_HELPER_TEXT}
                  placeholder="Type Email Address"
                  required
                />
              </Box>

              <Box mt={3}>
                <ClearVerifyStaffAccessFormPart disabled={submitting} />
              </Box>

              <Box display="flex" gap={2} mt={4} justifyContent="center">
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleClose}
                  disabled={submitting}
                >
                  Close
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={!valid || submitting}
                  loading={submitting}
                >
                  Send invite
                </Button>
              </Box>
            </React.Fragment>
          );
        }}
      </Form>
    </Box>
  );
};

const initialValues: InviteFormValues = {
  emails: [],
  role: ClearVerifyStaffRole.Manager,
  isFinancialReportAvailable: false,
  isRecipientForMonthlyFinancialReport: false,
};
